// The offline card data's storage (C5): its OWN IndexedDB database,
// `cb-offline-shared`, shared by every owner on the device. A2 card data is
// public, not owner data: it is never fenced, never per owner, and never
// wiped by a sign-out (C3 step 7 lists it as NOT owner-derived).
//
//   meta  'installed' -> InstalledMeta (what is installed and its sizes)
//   meta  'check'     -> CheckMeta (the last daily manifest check)
//   cards <version>   -> StoredCards (the verified gzip bytes + the built index)
//
// The raw artifact is kept compressed (about 6 MB) and re-parsed on load,
// which is faster than reading back 25 MB of expanded JSON; the index (the
// slow part to build) is stored ready to use.
import {
  META_CHECK, META_INSTALLED, SHARED_DB, SHARED_DB_VERSION, STORE_DATA, STORE_META, SUPPORTED_SCHEMA,
} from './offlineCards.codes'
import type { PersistedIndex } from './engine'
import type { A2Manifest } from './types'

export interface InstalledMeta {
  schema_version: number
  version: string
  manifest: A2Manifest
  installed_at: string
  index_format: number
  card_count: number
  /** Bytes stored: the gzip artifact and the index. */
  stored_bytes: { artifact: number; index: number }
}

export interface CheckMeta {
  schema_version: number
  checked_at: string
  manifest: A2Manifest | null
}

export interface StoredCards {
  schema_version: number
  version: string
  gz: ArrayBuffer
  index: PersistedIndex
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

/**
 * A transaction's outcome. It settles on `complete` or `abort` only: an
 * `error` event fires BEFORE the abort, while `tx.error` is still null, so
 * settling there lost the cause and a quota failure read as a generic
 * error. The first request error is kept as the fallback cause, so a
 * QuotaExceededError always reaches the caller (C5 rule 4: storage_full).
 */
export function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    let first: DOMException | null = null
    tx.oncomplete = () => resolve()
    tx.onerror = (ev: Event) => {
      const err = (ev.target as IDBRequest | null)?.error ?? null
      if (!first && err) first = err
    }
    // eslint-disable-next-line i18next/no-literal-string -- a DOMException name
    tx.onabort = () => reject(tx.error ?? first ?? new DOMException('transaction aborted', 'AbortError'))
  })
}

let dbPromise: Promise<IDBDatabase> | null = null

export function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined'
}

export function openSharedDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(SHARED_DB, SHARED_DB_VERSION)
    open.onupgradeneeded = () => {
      const db = open.result
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META)
      if (!db.objectStoreNames.contains(STORE_DATA)) db.createObjectStore(STORE_DATA)
    }
    open.onsuccess = () => {
      const db = open.result
      // Another tab upgrading later: step aside instead of blocking it.
      db.onversionchange = () => { db.close(); dbPromise = null }
      resolve(db)
    }
    open.onerror = () => { dbPromise = null; reject(open.error) }
  })
  return dbPromise
}

async function getMeta<T extends { schema_version: number }>(key: string): Promise<T | null> {
  const db = await openSharedDb()
  // eslint-disable-next-line i18next/no-literal-string -- an IndexedDB mode
  const value = await req(db.transaction(STORE_META, 'readonly').objectStore(STORE_META).get(key)) as T | undefined
  // A record of another schema is cache data: discarded (C5 rule 2).
  return value && value.schema_version === SUPPORTED_SCHEMA ? value : null
}

export const readInstalled = () => getMeta<InstalledMeta>(META_INSTALLED)
export const readCheck = () => getMeta<CheckMeta>(META_CHECK)

export async function writeCheck(manifest: A2Manifest | null, at: Date = new Date()): Promise<CheckMeta> {
  const db = await openSharedDb()
  const record: CheckMeta = { schema_version: SUPPORTED_SCHEMA, checked_at: at.toISOString(), manifest }
  // eslint-disable-next-line i18next/no-literal-string -- an IndexedDB mode
  const tx = db.transaction(STORE_META, 'readwrite')
  tx.objectStore(STORE_META).put(record, META_CHECK)
  await done(tx)
  return record
}

export async function readCards(version: string): Promise<StoredCards | null> {
  const db = await openSharedDb()
  // eslint-disable-next-line i18next/no-literal-string -- an IndexedDB mode
  const value = await req(db.transaction(STORE_DATA, 'readonly').objectStore(STORE_DATA).get(version)) as StoredCards | undefined
  return value && value.schema_version === SUPPORTED_SCHEMA ? value : null
}

/** Store a verified version and make it the installed one; older versions go. */
export async function writeInstall(cards: StoredCards, meta: InstalledMeta): Promise<void> {
  const db = await openSharedDb()
  // eslint-disable-next-line i18next/no-literal-string -- an IndexedDB mode
  const tx = db.transaction([STORE_DATA, STORE_META], 'readwrite')
  const data = tx.objectStore(STORE_DATA)
  const keys = await req(data.getAllKeys())
  for (const k of keys) if (k !== cards.version) data.delete(k)
  data.put(cards, cards.version)
  tx.objectStore(STORE_META).put(meta, META_INSTALLED)
  await done(tx)
}

/**
 * Drop the installed card data, ONLY if `version` is still the installed one
 * (read in the same transaction). A window that validated an old version
 * never deletes a newer install another window made meanwhile.
 */
export async function clearInstallIf(version: string): Promise<boolean> {
  const db = await openSharedDb()
  // eslint-disable-next-line i18next/no-literal-string -- an IndexedDB mode
  const tx = db.transaction([STORE_DATA, STORE_META], 'readwrite')
  const current = await req(tx.objectStore(STORE_META).get(META_INSTALLED)) as InstalledMeta | undefined
  const still = !!current && current.version === version
  if (still) {
    tx.objectStore(STORE_DATA).delete(version)
    tx.objectStore(STORE_META).delete(META_INSTALLED)
  }
  await done(tx)
  return still
}

/**
 * Replace the stored index of `version` (a repair), ONLY if `version` is
 * still the installed one, in the same transaction: a repair never writes
 * an old version back over a newer install.
 */
export async function writeIndexIf(cards: StoredCards, meta: InstalledMeta): Promise<boolean> {
  const db = await openSharedDb()
  // eslint-disable-next-line i18next/no-literal-string -- an IndexedDB mode
  const tx = db.transaction([STORE_DATA, STORE_META], 'readwrite')
  const current = await req(tx.objectStore(STORE_META).get(META_INSTALLED)) as InstalledMeta | undefined
  const still = !!current && current.version === cards.version && meta.version === cards.version
  if (still) {
    tx.objectStore(STORE_DATA).put(cards, cards.version)
    tx.objectStore(STORE_META).put(meta, META_INSTALLED)
  }
  await done(tx)
  return still
}
